import { Plugin, App } from 'vue'
import { Router } from 'vue-router'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { getUserRole } from '@/shared/composables/local-storage'
import { useStoreEnvSettings } from '@/shared/store/environment'
import { isDevelopment, isElectron, tauriMode } from '@/shared/utils/import-meta'

import { YandexMetrikaConfig, YandexMetrikaInit } from './types'
import { loadScript } from './utils'

export const YandexMetrikaPlugin: Plugin = {
  install: (app: App, { router }: { router: Router }) => {
    const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname)
    if (isDevelopment() || tauriMode() || isElectron() || isLocalHost) {
      return
    }

    const { getSettingByKey, onInitialized } = useStoreEnvSettings()
    onInitialized().then(() => {
      const id = getSettingByKey('yandexMetrikaId')?.value
      if (!id) {
        return
      }

      const { currentUser } = useCurrentUser()
      const { currentGuest } = useGuestAuth()
      const scriptSrc = getSettingByKey('yandexMetrikaScriptSrc')?.value ?? 'https://mc.yandex.ru/metrika/tag.js'
      const config: YandexMetrikaConfig = {
        id,
        options: {
          accurateTrackBounce: true,
          clickmap: false,
          defer: false,
          ecommerce: false,
          params: [],
          userParams: {
            id: currentUser.value?.id,
            name: currentUser.value?.name ?? currentGuest.value?.pServed ?? 'Anonymous',
            role: getUserRole(),
          },
          trackHash: false,
          trackLinks: false,
          type: 0,
          webvisor: false,
          triggerEvent: false,
        },
        router,
        ignoreRoutes: [],
        skipSamePath: true,
      }
      const loadFn = () => loadScript(() => {
        const init = {
          id: config.id,
          ...config.options,
        }
        const Ya = window.Ya as unknown as {
          Metrika2: YandexMetrikaInit
        }
        const metrika = new Ya.Metrika2(init)
        router.afterEach(function (to, from) {

          // check if route is in ignoreRoutes
          if (config.ignoreRoutes && (config.ignoreRoutes.includes(to.path))) { return }

          // do not track page visit if previous and next routes URLs match
          if (config.skipSamePath && to.path == from.path) { return }

          metrika.hit(to.path, { referer: from.path })
        })
      }, scriptSrc)
      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadFn)
      } else {
        setTimeout(loadFn, 5000)
      }
    })
  },
}
